import { AudienceType } from '@/types/audience';
import React, { createContext, useContext, useState, ReactNode } from 'react';

// Interface for competitor data
export interface CompetitorData {
  name: string;
  url: string;
  similarities?: string;
  differences?: string;
}

// Interface for uploaded file content
export interface UploadedFileContent {
  name: string;
  content: string;
  type: string;
  size: number;
}

// Interface for chat messages
export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
  type?: 'impossible_request';
  explanation?: string;
  follow_up_questions?: string[];
  partial_data_available?: string;
}

// Interface for sample persona data
export interface SamplePersona {
  id: string;
  name: string;
  image: string | null;
  age: number;
  gender: string;
  location: string;
  occupation: string;
  job_title: string;
  role: string;
  income: string;
  education: string;
  company: string;
  interests: string[];
  demographics: {
    tenure: string;
    annualSpend_USD: number;
    shoppingIntensity: string;
    primaryDevice: string;
  };
  psychographics: {
    personality_OCEAN: {
      openness: number;
      conscientiousness: number;
      extraversion: number;
      agreeableness: number;
      neuroticism: number;
    };
    cognitiveStyle: string;
    values: string[];
    motivators: string[];
  };
  behaviors_and_needs: {
    goals: string[];
    pain_points: string[];
    expected_outcomes: string[];
    concerns_and_obstacles: string[];
    decision_criteria: string[];
    path_to_purchase: string[];
    customer_profile_insights: {
      triggers_and_motivations: string[];
      reaction_to_failure: string;
      trust_signals: string[];
      behavioral_resistance: string[];
    };
    human_chaos: {
      emotional_triggers: string[];
      contradiction_patterns: string[];
      real_moments: string[];
      decision_making_style: string;
      shopping_mood_states: {
        stress_response: string;
        celebration_mode: string;
        boredom_behavior: string;
        guilt_purchases: string;
      };
    };
    marketing_insights: {
      segment_motivation: string;
      message_receptivity: string;
      trust_hierarchy: string;
      purchase_barriers: string;
      hero_image_response: string;
    };
  };
  segmentId?: number | string | null;
}

export interface BuildAudienceData {
  simulationTarget: AudienceType | null;
  audienceScope: 'all' | 'specific' | null;
  websiteUrl: string;
  specificSegment: string;
  qualitativeInfo: string;
  uploadedFile: File | null;
  uploadedFileContent: UploadedFileContent | null;
  foundationalResearch: string;
  businessModel: 'B2B' | 'B2C' | null;
  userProvidedCompetitors: CompetitorData[];
  competitors: CompetitorData[];
  segments: Record<string, any>[];
  globalChatHistory: ChatMessage[];
  segmentationFocus?: string;
  segmentationApproach?: string;
  defineSubStep?: number; // Track current sub-step within AudienceDefine
  questionnaireSubStep?: number; // Track current sub-step within AudienceQuestionnaire
  finalizeSubStep?: number; // Track current sub-step within AudienceFinalize
  lastModifiedStep?: number;
  audienceName?: string;
  totalAudienceSize?: number;
  segmentPercentages?: Record<string, number>;
  submissionTaskId?: string;
}

interface BuildAudienceContextType {
  buildAudienceData: BuildAudienceData;
  setBuildAudienceData: React.Dispatch<React.SetStateAction<BuildAudienceData>>;
  updateBuildAudienceData: (data: Partial<BuildAudienceData>, stepNumber?: number) => void;
  resetBuildAudienceData: () => void;
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  isGenerating: boolean;
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>;
}

const initialBuildAudienceData: BuildAudienceData = {
  simulationTarget: null,
  audienceScope: null,
  websiteUrl: '',
  specificSegment: '',
  qualitativeInfo: '',
  uploadedFile: null,
  uploadedFileContent: null,
  foundationalResearch: '',
  businessModel: null,
  userProvidedCompetitors: [
    { name: '', url: '' },
    { name: '', url: '' },
    { name: '', url: '' }
  ],
  competitors: [],
  segments: [],
  globalChatHistory: [],
  segmentationFocus: '',
  segmentationApproach: '',
  lastModifiedStep: undefined,
};

const BuildAudienceContext = createContext<BuildAudienceContextType | undefined>(undefined);

// Deep equality utility function
const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  
  if (a == null || b == null) return a === b;
  
  if (typeof a !== typeof b) return false;
  
  if (typeof a !== 'object') return a === b;
  
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  
  if (keysA.length !== keysB.length) return false;
  
  for (const key of keysA) {
    if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false;
  }
  
  return true;
};

// Step dependency mapping - which fields belong to which step (true 3-step flow)
const STEP_FIELDS = {
  1: ['websiteUrl'], // Step 1: Company URL input
  2: ['segmentationFocus'], // Step 2: Customer segments focus
  3: ['segmentationApproach'] // Step 3: Segmentation approach + build
};

// Get all fields that should be cleared when a step is modified
const getFieldsToClear = (modifiedStep: number): string[] => {
  const fieldsToClear: string[] = [];

  // Clear all fields from steps after the modified step (simplified 3-step flow)
  for (let step = modifiedStep + 1; step <= 3; step++) {
    fieldsToClear.push(...STEP_FIELDS[step as keyof typeof STEP_FIELDS]);
  }

  return fieldsToClear;
};

export const BuildAudienceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [buildAudienceData, setBuildAudienceData] = useState<BuildAudienceData>(initialBuildAudienceData);
  const [currentStep, setCurrentStep] = useState<number>(101);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const updateBuildAudienceData = (data: Partial<BuildAudienceData>, stepNumber?: number) => {
    setBuildAudienceData(prev => {
      let newData = { ...prev, ...data };
      
      // If a step number is provided, check if any fields for that step actually changed
      if (stepNumber) {
        const stepFields = STEP_FIELDS[stepNumber as keyof typeof STEP_FIELDS] || [];
        
        // Check if any fields from this step have actually changed
        const hasStepDataChanged = stepFields.some((field: string) => {
          const oldValue = prev[field as keyof BuildAudienceData];
          const newValue = data[field as keyof BuildAudienceData];
          // Only check fields that are being updated in this call
          return newValue !== undefined && !deepEqual(oldValue, newValue);
        });
        
        // Only invalidate if data actually changed for this step
        if (hasStepDataChanged) {
          const fieldsToClear = getFieldsToClear(stepNumber);
          
          // Clear the dependent fields by resetting them to their initial values
          fieldsToClear.forEach(field => {
            if (field === 'segmentationFocus' || field === 'segmentationApproach' || field === 'audienceName') {
              newData = { ...newData, [field]: '' };
            } else if (field === 'segments') {
              newData = { ...newData, [field]: [] };
            }
          });
          
          // Update the last modified step
          newData.lastModifiedStep = stepNumber;
        }
      }
      
      return newData;
    });
  };

  const resetBuildAudienceData = () => {
    setBuildAudienceData(initialBuildAudienceData);
    setCurrentStep(101);
  };

  return (
    <BuildAudienceContext.Provider 
      value={{ 
        buildAudienceData, 
        setBuildAudienceData, 
        updateBuildAudienceData,
        resetBuildAudienceData,
        currentStep,
        setCurrentStep,
        isGenerating,
        setIsGenerating
      }}
    >
      {children}
    </BuildAudienceContext.Provider>
  );
};

export const useBuildAudience = () => {
  const context = useContext(BuildAudienceContext);
  if (context === undefined) {
    throw new Error('useBuildAudience must be used within a BuildAudienceProvider');
  }
  return context;
};
